import { toast } from "sonner";

import { ArgumentError, OperationError, TemplateLineError, UserError } from "@/__generated__/graphql";
import { isArgumentError, isOperationError, isTemplateLineError, isUserError } from "@/utils/typeGuards";

function argumentErrorsHandler(error: unknown, callback: (e: ArgumentError) => void) {
  if (Array.isArray(error)) {
    error.forEach(e => {
      if (isArgumentError(e)) {
        callback(e);
      }
    });
  }
}

function operationErrorsHandler(error: unknown, callback: (e: OperationError) => void) {
  if (Array.isArray(error)) {
    error.forEach(e => {
      if (isOperationError(e)) {
        callback(e);
      }
    });
  }
}

function templateLineErrorsHandler(error: unknown, callback: (e: TemplateLineError[]) => void) {
  if (Array.isArray(error)) {
    callback(error.filter(e => isTemplateLineError(e)));
  }
}

export function assertHasNoUserErrors(userErrors?: UserError[]): asserts userErrors {
  if (userErrors?.length) {
    throw userErrors; // make error type for this
  }
}

interface ErrorCallbacks {
  onOperationError?: (e: OperationError) => void;
  onArgumentError?: (e: ArgumentError) => void;
  onTemplateLineErrors?: (e: TemplateLineError[]) => void;
  onBasicError?: (e?: Error) => void;
}

const defaultOperationErrorHandler = (e: OperationError) => {
  toast.error("Error occurred", {
    description: e.message,
  });
};
const defaultInputErrorHandler = (e: UserError) => {
  toast.error("Invalid input", {
    description: e.message,
  });
};

export default function legacyErrorHandler(
  error: unknown,
  {
    onOperationError,
    onArgumentError,
    onTemplateLineErrors = errors => errors.forEach(e => defaultInputErrorHandler(e)),
    onBasicError,
  }: ErrorCallbacks = {}
) {
  if (isUserError(error)) {
    operationErrorsHandler(error, onOperationError || defaultOperationErrorHandler);
    argumentErrorsHandler(error, onArgumentError || defaultInputErrorHandler);
    templateLineErrorsHandler(error, onTemplateLineErrors);
  } else if (error instanceof Error) {
    // handle basic error
    if (onBasicError) {
      onBasicError(error);
    }

    toast.error(error.name, {
      description: error.message,
    });
  } else {
    throw Error;
  }
}
